import { clsx } from '@payaca/utilities/cvaMerge';
import { FC, forwardRef, useEffect, useState } from 'react';

type AvailableColours =
  | 'black'
  | 'gray'
  | 'green'
  | 'blue'
  | 'red'
  | 'yellow'
  | 'purple'
  | 'white';

type ProgressBarBaseProps = { progress: number; total: number };

type TProgressBarProps =
  | (ProgressBarBaseProps & { colour?: AvailableColours })
  | (ProgressBarBaseProps & {
      colour: 'custom';
      // use bg-x-xxx class, e.g. bg-green-500
      customColourClass: string;
    });

export interface ProgressProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  bars: TProgressBarProps[];
}

const getBarColourStyle = (colour: AvailableColours) => {
  switch (colour) {
    case 'black':
      return 'bg-gray-800 dark:bg-white';
    case 'gray':
      return 'bg-gray-500 dark:bg-gray-500';
    case 'green':
      return 'bg-green-500 dark:bg-green-500';
    case 'blue':
      return 'bg-blue-500';
    case 'red':
      return 'bg-red-500';
    case 'yellow':
      return 'bg-yellow-500';
    case 'purple':
      return 'bg-purple-500';
    case 'white':
      return 'bg-white';
  }
};

const Progress = forwardRef<HTMLDivElement, ProgressProps>((props, ref) => {
  const { className, size = 'sm', bars = [] } = props;

  let sizeClass = '';
  switch (size) {
    case 'sm':
      sizeClass = 'h-1.5';
      break;
    case 'md':
      sizeClass = 'h-4';
      break;
    case 'lg':
      sizeClass = 'h-6';
      break;
  }

  return (
    <>
      <div
        ref={ref}
        className={clsx(
          'relative flex w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700',
          sizeClass,
          className
        )}
      >
        {bars.toReversed().map((bar, index) => (
          <ProgressBar key={index} {...bar} />
        ))}
      </div>
    </>
  );
});

const ProgressBar: FC<TProgressBarProps> = (props) => {
  const { progress, total, colour } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  let barColour = '';
  if (colour === 'custom') {
    barColour = props.customColourClass;
  } else {
    barColour = getBarColourStyle(colour || 'black');
  }

  const percentage = (progress / total) * 100;

  return (
    <div
      className={clsx(
        'absolute left-0 top-0 flex h-full flex-col justify-center overflow-hidden whitespace-nowrap rounded-full text-center text-xs text-white transition-[width] duration-1000',
        barColour
      )}
      role="progressbar"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={total}
      style={{ width: mounted ? `${percentage}%` : '0%' }}
    />
  );
};

export default Progress;
